import moment from "moment"
import { type FC, useEffect, useMemo, useState } from "react"
import { Table } from "react-bootstrap"
import LDSEllipsis from "src/components/common/LDS/LDSEllipsis/LDSEllipsis"
import { CERT_STATUS, type EmployeeCertificationElem, type EmployeeCertificationSaveModel } from "src/pages/hr/Types/TrainingTypes"
import "./CertificationTable.css"

interface CertificationTableProps {
    certs: EmployeeCertificationElem[]
    readOnly: boolean
    onSave: (certs: EmployeeCertificationSaveModel[]) => Promise<void>
    title?: string
}

type CertGroup = {
    groupKey: string
    groupName: string
    certs: EmployeeCertificationElem[]
}

const computeExpirationDate = (writtenTestDate: string | null, expirationType: number): moment.Moment | null => {
    if (expirationType === 5) return moment("2099-12-31T23:59:59")
    if (!writtenTestDate) return null
    const d = moment(writtenTestDate)
    switch (expirationType) {
        case 0: return d.clone().add(3, "years")
        case 1: return moment({ year: d.year() + 2 }).subtract(1, "second")
        case 2: return moment({ year: d.year() + 4 }).subtract(1, "second")
        case 3: return d.clone().add(1, "year")
        case 4: return moment({ year: d.year() + 1 }).subtract(1, "second")
        case 6: return d.clone().add(4, "years")
        case 7: return d.clone().add(3, "years")
        case 8: return d.clone().add(5, "years")
        case 9: return moment({ year: d.year() + 6 }).subtract(1, "second")
        default: return moment()
    }
}

const CertificationTable: FC<CertificationTableProps> = ({ certs, readOnly, onSave, title }) => {
    const [localCerts, setLocalCerts] = useState<EmployeeCertificationElem[]>(certs)
    const [dirtyIds, setDirtyIds] = useState<Set<number>>(new Set())
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setLocalCerts(certs)
        setDirtyIds(new Set())
    }, [certs])

    const groups: CertGroup[] = useMemo(() => {
        const map = new Map<string, CertGroup>()
        for (const cert of localCerts) {
            const key = cert.CertSubcategoryId != null
                ? `sub-${cert.CertSubcategoryId}`
                : `cat-${cert.CertCategoryId}`
            const name = cert.CertSubcategoryName ?? cert.CertCategoryName
            if (!map.has(key)) {
                map.set(key, { groupKey: key, groupName: name, certs: [] })
            }
            map.get(key)?.certs.push(cert)
        }
        return Array.from(map.values())
    }, [localCerts])

    const markDirty = (id: number) => {
        setDirtyIds(prev => new Set(prev).add(id))
    }

    const handleStatusChange = (cert: EmployeeCertificationElem, statusId: number) => {
        const updated = { ...cert, EmployeeCertificationStatusTypeId: statusId }
        setLocalCerts(prev => prev.map(c => c.EmployeeCertificationId === cert.EmployeeCertificationId ? updated : c))
        markDirty(cert.EmployeeCertificationId)
    }

    const handleDateChange = (cert: EmployeeCertificationElem, isoDate: string | null) => {
        const updated = { ...cert, WrittenTestDate: isoDate }
        setLocalCerts(prev => prev.map(c => c.EmployeeCertificationId === cert.EmployeeCertificationId ? updated : c))
        markDirty(cert.EmployeeCertificationId)
    }

    const handleSave = async () => {
        if (!dirtyIds.size || saving) return
        const toSave = localCerts
            .filter(c => dirtyIds.has(c.EmployeeCertificationId))
            .map(c => ({
                EmployeeCertificationId: c.EmployeeCertificationId,
                EmployeeId: c.EmployeeId,
                CertificationId: c.CertificationId,
                EmployeeCertificationStatusTypeId: c.EmployeeCertificationStatusTypeId,
                WrittenTestDate: c.WrittenTestDate,
                HasBadge: c.HasBadge,
                BadgeIssueDate: c.BadgeIssueDate,
                BadgeRenewalDate: c.BadgeRenewalDate,
            }))
        setSaving(true)
        try {
            await onSave(toSave)
            setDirtyIds(new Set())
        } finally {
            setSaving(false)
        }
    }

    if (localCerts.length === 0) {
        return (
            <div className="CertificationTableEmpty">
                <p>No certifications found.</p>
            </div>
        )
    }

    return (
        <div className="CertificationTableWrapper">
            {(title || !readOnly) && (
                <div className="CertificationTableActions">
                    {title && <span className="CertificationTableTitle">{title}</span>}
                    {!readOnly && (
                        <button
                            type="button"
                            className="cert-save-button"
                            disabled={!dirtyIds.size || saving}
                            onClick={handleSave}
                        >
                            {saving ? <LDSEllipsis color="#555" /> : "Save Changes"}
                        </button>
                    )}
                </div>
            )}

            {groups.map(group => (
                <div key={group.groupKey} className="CertificationTableGroup">
                    <h4 className="CertificationTableGroupTitle">{group.groupName}</h4>

                    <Table bordered hover size="sm" className="CertificationTable">
                        <thead>
                            <tr>
                                <th className="cert-col-name">Record of Training</th>
                                <th className="cert-col-status">Y</th>
                                <th className="cert-col-status">N</th>
                                <th className="cert-col-status">N/A</th>
                                <th className="cert-col-date">Test Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {group.certs.map(cert => {
                                const status = cert.EmployeeCertificationStatusTypeId
                                const testDateInput = cert.WrittenTestDate
                                    ? moment(cert.WrittenTestDate).format("YYYY-MM-DD")
                                    : ""
                                const displayDate = testDateInput
                                    ? moment(testDateInput, "YYYY-MM-DD").format("M/D/YYYY")
                                    : "—"
                                const expDate = computeExpirationDate(cert.WrittenTestDate, cert.ExpirationType)
                                const isExpired = expDate != null && expDate.isBefore(moment())

                                return (
                                    <tr key={cert.EmployeeCertificationId} className={isExpired ? "cert-row-expired" : undefined}>
                                        <td className="cert-col-name">{cert.CertificationName}</td>

                                        {([CERT_STATUS.YES, CERT_STATUS.NO, CERT_STATUS.NA] as const).map(statusId => (
                                            <td key={statusId} className="cert-col-status">
                                                <input
                                                    type="radio"
                                                    name={`cert-status-${cert.EmployeeCertificationId}`}
                                                    checked={status === statusId}
                                                    disabled={readOnly || saving}
                                                    onChange={() => handleStatusChange(cert, statusId)}
                                                    className="cert-radio"
                                                />
                                            </td>
                                        ))}

                                        <td className="cert-col-date">
                                            {readOnly ? (
                                                <span>{displayDate}</span>
                                            ) : (
                                                <input
                                                    type="date"
                                                    className="cert-date-input"
                                                    value={testDateInput}
                                                    disabled={saving}
                                                    onChange={e => {
                                                        const val = e.target.value
                                                        if (val) {
                                                            handleDateChange(cert, moment(val).toISOString())
                                                        } else {
                                                            handleDateChange(cert, null)
                                                        }
                                                    }}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </Table>
                </div>
            ))}
        </div>
    )
}

export default CertificationTable
